import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { smoothScroll } from '../utils/smoothScroll';
import logo from '../assets/images/logo.png';

function Header() {

  const { auth, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const location = useLocation();
  const navigate = useNavigate();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  const handleMouseMove = useCallback((e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  }, []);

  const handleNavigation = useCallback((path) => {
    console.log('handleNavigation called with path:', path);
    console.log('Current pathname:', location.pathname);
    
    if (location.pathname === path) {
      console.log('Already on page, calling smoothScroll');
      // If we're already on the page, scroll to top
      smoothScroll('html', 0);
    } else {
      console.log('Navigating to new page');
      // If navigating to a new page, use React Router
      navigate(path);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleScroll, handleMouseMove]);

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed top-0 left-0 right-0 z-50 pt-6"
    >
      {/* Custom Cursor */}
      <div 
        className="fixed pointer-events-none z-50"
        style={{
          left: `${mousePosition.x}px`,
          top: `${mousePosition.y}px`,
          transform: 'translate(-50%, -50%)',
          transition: 'transform 0.1s ease-out',
        }}
      >
        <div className="w-3 h-3 rounded-full bg-white/80"></div>
        <div className="w-6 h-6 rounded-full border border-white/30 absolute -top-1.5 -left-1.5 animate-ping"></div>
      </div>

      <motion.nav 
        className={`max-w-[1200px] mx-auto flex justify-between items-center px-12 py-5 rounded-[100px] transition-colors duration-300 ${
          isScrolled 
            ? 'bg-black/95 backdrop-blur-lg shadow-2xl' 
            : 'bg-transparent'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center mr-24">
          <Link to="/" className="group" onClick={() => handleNavigation('/')}>
            <img 
              src={logo} 
              alt="LeadTrek Logo" 
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
              loading="eager"
            />
          </Link>
        </div>

        {/* Navigation Links */}
        <div className="flex items-center space-x-16">
          {[
            { path: '/', label: 'Home' },
            { path: '/product', label: 'Product' },
            { path: '/pricing', label: 'Pricing' },
            { path: '/about-us', label: 'About' },
            { path: '/blog', label: 'Blog' }
          ].map((item) => (
            <div 
              key={item.path}
              className="relative group"
            >
              <Link 
                to={item.path}
                onClick={(e) => {
                  console.log('Link clicked:', item.path);
                  if (location.pathname === item.path) {
                    e.preventDefault();
                    console.log('Same page, scrolling to top');
                    smoothScroll('html', 0);
                  }
                }}
                className={`text-base font-medium transition-all duration-300 ${
                  location.pathname === item.path 
                    ? 'text-white' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
              {location.pathname === item.path && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#C334F6] to-[#543DF4]" />
              )}
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#C334F6] to-[#543DF4] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* Auth Buttons */}
        {auth.isAuthenticated ? (
          <div className="flex items-center space-x-8 ml-24">
            <div className="relative group">
              <Link 
                to="/dashboard/broker/"
                className="text-base font-medium text-white/70 hover:text-white transition-all duration-300"
              >
                Dashboard
              </Link>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#C334F6] to-[#543DF4] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <button
              onClick={logout}
              className="relative overflow-hidden bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white px-8 py-3 rounded-full text-base font-medium hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="relative z-10">Logout</span>
            </button>
          </div>
        ) : (
          <div className="ml-24">
            <Link 
              to="/login"
              className="relative overflow-hidden bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white px-8 py-3 rounded-full text-base font-medium hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="relative z-10">Login</span>
            </Link>
          </div>
        )}
      </motion.nav>
    </motion.header>
  );
}

export default Header;